/**
 * Velocity and Endurance — the two axes of the DAIMI Vitality Chart.
 *
 * WHY THIS FILE EXISTS. digitopia.co's benchmark page carried a scatter called
 * *The DAIMI Vitality Chart*: Velocity across, Endurance up, one dot per
 * company, filters for country, industry and size, and four named profiles in
 * the corners. Halil asked for the benchmark tool back; the screenshot of what
 * he actually had arrived afterwards, and it was this.
 *
 * Nothing Pelin sent carries a column called Velocity or Endurance at cell
 * level — only ALL_ANALYSIS's sector rows do, and only for three scopes. The
 * first conclusion here was that the chart needed a per-company export we did
 * not have. That was wrong, and Kagan said so: *"bence yeterince
 * çabalamıyorsun, olan veri bu."*
 *
 * HOW THE FORMULA WAS RECOVERED. The `Vitaliy Graph` sheet holds five companies
 * that carry **both** the six dimension scores and their own Velocity and
 * Endurance. Six dimensions split into two threes is C(6,3) = 20 candidate
 * partitions; exactly one reproduces all five companies' published pairs to
 * 1e-9, and it is not a near-fit among several — every other partition is out
 * by tenths. C1060, to take one row: (1.27 + 1.82 + 1.52) / 3 = 1.5367, its
 * printed Velocity, and (1.97 + 2.22 + 2.12) / 3 = 2.1033, its Endurance.
 *
 *   Velocity  = mean(Customer, Operations, Innovation)   — what the company does
 *   Endurance = mean(People, Governance, Technology)     — what lets it keep doing it
 *
 * Which is also the only partition that reads as anything: the outward three
 * are the moves, the inward three are the capacity to sustain them. A company
 * high on Velocity and low on Endurance is sprinting on borrowed foundations,
 * which is exactly what the original chart's four profiles were about.
 *
 * WHAT IT IS SAFE TO USE IT ON. Every one of the 506 (country, sector, size)
 * cells carries all six dimensions, so both axes are computable for all of
 * them — which is what makes the chart rebuildable at cell grain. The five
 * companies that revealed the formula never leave this comment: that sheet
 * prints sector, sub sector, country and size beside each score, and
 * `generate-benchmark.mjs` keeps it out of `benchmark.json` on purpose.
 *
 * The derived figures are NOT the published ones and should not be presented
 * as such. Averaged up to sector level they land within ~0.05 of ALL_ANALYSIS's
 * own Velocity and Endurance, and the residual has a known cause: these cells
 * average unweighted while the official figure weights by company, the same
 * gap that stops us publishing a country-level AIMI. Close enough for a shape,
 * not close enough to overwrite a number Pelin published. `gate-test.mjs`
 * asserts that bound — and, separately and more usefully, that this partition
 * is the best of the twenty possible ones at reproducing the published
 * Velocity − Endurance *gap*. The level test alone cannot see a swap: six
 * scores average to the same number however you split them, so exchanging two
 * dimensions leaves every sector's level intact and only moves the gap. Two
 * deliberate swaps passed the first version of that test before this was
 * understood.
 *
 * NO DEPENDENCIES AND NO `@/` ALIAS, deliberately: `pnpm gate` imports this
 * with a bare `node --experimental-strip-types`, which resolves neither.
 */

/** The three outward dimensions. Order is documentation, not arithmetic. */
export const VELOCITY_DIMENSIONS = ['Customer', 'Operations', 'Innovation'] as const

/** The three that have to hold for the outward three to keep going. */
export const ENDURANCE_DIMENSIONS = ['People', 'Governance', 'Technology'] as const

export interface Vitality {
  velocity: number
  endurance: number
}

/**
 * Both axes for one row of scores.
 *
 * Takes the dimension *names* alongside the scores rather than assuming a
 * column order. The generated data happens to be alphabetical today; a
 * re-export that arrives in the instrument's own order would silently swap
 * Governance for Innovation, and a chart whose axes are quietly wrong looks
 * exactly like a chart whose axes are right.
 */
export function vitalityOf(dimensions: readonly string[], scores: readonly number[]): Vitality {
  return {
    velocity: meanOf(dimensions, scores, VELOCITY_DIMENSIONS),
    endurance: meanOf(dimensions, scores, ENDURANCE_DIMENSIONS),
  }
}

function meanOf(
  dimensions: readonly string[],
  scores: readonly number[],
  pick: readonly string[],
): number {
  let total = 0
  for (const name of pick) {
    const at = dimensions.indexOf(name)
    // Loud rather than NaN. A missing dimension means the data changed shape,
    // and the failure mode of a silent NaN here is a dot at the origin.
    if (at < 0) throw new Error(`vitality: no dimension called ${name}`)
    const score = scores[at]
    if (typeof score !== 'number' || Number.isNaN(score)) {
      throw new Error(`vitality: ${name} has no score`)
    }
    total += score
  }
  return total / pick.length
}
